import {useEffect, useRef} from 'react';
import {useAtomValue, useSetAtom} from 'jotai';
import {useRouter, usePathname} from '@/hooks/useAppRouter';
// SWRConfig is available but not currently used
import {userAtom, getUserAtom} from '@/store/user';
import {addToastAtom} from '@/store/toast';
import {getProductsAtom} from '@/store/products';
import {getGroupsAtom} from '@/store/groups';
import {getMaterialsAtom} from '@/store/materials';
import {getMaterialSuppliersAtom} from '@/store/materials/suppliers';
import {getBrandsAtom} from '@/store/brands';
import {
    getUserStatsAtom,
    getUserUsageAtom,
    getRecentRendersAtom,
} from '@/store/dashboard';
import {setBrandingCssVariables} from '@/actions/user';
import {setFpAuthToken} from '@/lib/linda-jwt';
import {useBackgroundPoller} from '@/hooks/useBackgroundPoller';
import {useNotifications} from '@/hooks/useNotifications';

const PUBLIC_ROUTES = ['/login', '/forgot-password'];

export function AppInitializer() {
    const user = useAtomValue(userAtom);
    const loadUser = useSetAtom(getUserAtom);
    const loadMaterialSuppliers = useSetAtom(getMaterialSuppliersAtom);
    const loadUserStats = useSetAtom(getUserStatsAtom);
    const loadUserUsage = useSetAtom(getUserUsageAtom);
    const loadRenders = useSetAtom(getRecentRendersAtom);
    const loadProducts = useSetAtom(getProductsAtom);
    const loadGroups = useSetAtom(getGroupsAtom);
    const loadMaterials = useSetAtom(getMaterialsAtom);
    const loadBrands = useSetAtom(getBrandsAtom);
    const addToast = useSetAtom(addToastAtom);
    const authInitializedRef = useRef(false);
    const tokenValidationRef = useRef(false);
    const cacheInitializedRef = useRef(false);
    const router = useRouter();
    const pathname = usePathname();

    // Initialize notification system and background poller
    useNotifications();
    useBackgroundPoller();

    // Initialize cache on mount
    useEffect(() => {
        if (cacheInitializedRef.current) return;
        cacheInitializedRef.current = true;
    }, [user]);

    // In development, sync auth token to a cookie so all API requests
    // (fetch, XHR) are automatically authenticated via requireAuth().
    // Production uses cookies set by the login flow.
    useEffect(() => {
        if (import.meta.env.PROD) return;
        if (!user?.auth_token) return;
        document.cookie = `local_auth_token=${user.auth_token}; path=/; SameSite=Lax`;
    }, [user?.auth_token]);

    useEffect(() => {
        if (authInitializedRef.current) return;

        authInitializedRef.current = true;

        async function checkAuth() {
            if (user) return;

            try {
                const fetchedUser = await loadUser();
                // Null means unauthenticated (e.g. window.MB_CONTEXT.user = null)
                if (!fetchedUser && !PUBLIC_ROUTES.includes(pathname)) {
                    router.push('/login');
                }
            } catch {
                if (!PUBLIC_ROUTES.includes(pathname)) router.push('/login');
            }
        }

        checkAuth();
    }, [loadUser, router, pathname, user]);

    useEffect(() => {
        if (!user) return;

        if (!user.modelbank_token && !tokenValidationRef.current) {
            tokenValidationRef.current = true;
            addToast({
                duration: 999999999,
                type: 'error',
                message:
                    'Your account is missing a Modelbank Token. Please contact support to resolve this issue.',
            });

            return;
        }

        setBrandingCssVariables(user.branding);
        setFpAuthToken(user.auth_token);

        // Lazy load data based on current route
        if (pathname === '/') {
            // Load dashboard data only on homepage
            loadUserStats();
            loadUserUsage();
            loadRenders();
            loadBrands();
            loadProducts(); // Needed for "Recently Added Products" widget
            loadGroups(); // Needed for Groups stat in sidebar
            loadMaterials(); // Needed for Materials stat in sidebar
        } else if (pathname.startsWith('/products')) {
            loadProducts();
            loadBrands();
        } else if (pathname.startsWith('/materials')) {
            loadMaterialSuppliers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, pathname]);

    return null;
}
